import { useEffect, useMemo, useState } from "react";
import Modal from "../../../components/base/Modal";
import type { buildThemeClasses } from "../theme";
import { fetchLeads, type Lead } from "../services/leadsService";
import { fetchAgents, type Agent } from "../services/agentsService";
import { leadPhone } from "../services/leadPhone";

// ============================================================================
// PlaceTestCallModal — pick an EXISTING lead and place a call to it on demand,
// so we can test the AI agent (or a human softphone call) without creating a
// fresh lead every time. Lets you choose:
//   • the lead (searchable directory of existing leads with a phone),
//   • the call type (IA agent  |  Humano / softphone),
//   • the agent (AI agents for IA — the inbound qualification agent this module
//     works with is pre-selected; human agents, optional, for a human call).
// AI calls go through onPlaceAi (POST /crm/calls/place); human calls hand the
// selected lead to onStartHuman, which boots the browser softphone.
// ============================================================================

// A lead is callable only if it carries a phone (whatsapp or plain phone) —
// `leadPhone` in services/leadPhone.ts is the shared definition.

export default function PlaceTestCallModal({
  tc,
  isDarkMode,
  onClose,
  onPlaceAi,
  onStartHuman,
}: {
  tc: ReturnType<typeof buildThemeClasses>;
  isDarkMode: boolean;
  onClose: () => void;
  /** Place an AI call; should throw on failure so the modal can stay open. */
  onPlaceAi: (leadId: string, crmAgentId?: string) => Promise<void>;
  /** Start a human softphone call to the lead (the modal closes right after). */
  onStartHuman: (lead: Lead, crmAgentId?: string) => void;
}) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [callType, setCallType] = useState<"ai" | "human">("ai");
  const [agentId, setAgentId] = useState<string>("");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the lead directory + the agent roster once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [leadPage, agentPage] = await Promise.all([
          fetchLeads({ limit: 200 }),
          fetchAgents({ limit: 300 }),
        ]);
        if (!alive) return;
        setLeads(leadPage.data);
        setAgents(agentPage.data);
      } catch (e) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        if (alive) setError(err.response?.data?.message || err.message || "No se pudo cargar el directorio");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Active agents for the current call type. Real (provisioned) agents come
  // first — the demo-seed agents (`seed_*`) carry placeholder ElevenLabs ids that
  // aren't provisioned, so a call with them 404s. Among real agents, inbound
  // qualifiers (useCase "confirm_lead") are surfaced first.
  const isDemo = (a: Agent) => a.id.startsWith("seed_");
  const typeAgents = useMemo(() => {
    const wantType = callType === "ai" ? "ai" : "human";
    return agents
      .filter((a) => a.type === wantType && a.status === "active")
      .sort((x, y) => {
        if (isDemo(x) !== isDemo(y)) return isDemo(x) ? 1 : -1; // real first
        return Number(y.useCase === "confirm_lead") - Number(x.useCase === "confirm_lead");
      });
  }, [agents, callType]);

  // Keep a valid agent selected as the call type / roster changes.
  useEffect(() => {
    setAgentId((prev) => (typeAgents.some((a) => a.id === prev) ? prev : typeAgents[0]?.id || ""));
  }, [typeAgents]);

  const callableLeads = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads
      .filter((l) => leadPhone(l)) // only leads we can actually dial
      .filter((l) => {
        if (!q) return true;
        return [l.name, l.company, l.whatsapp, l.phone, l.email]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
  }, [leads, query]);

  const selectedLead = leads.find((l) => l.id === selectedLeadId) || null;
  const selectedAgentIsDemo = callType === "ai" && !!agentId && agentId.startsWith("seed_");

  const canPlace =
    !!selectedLead &&
    !placing &&
    (callType === "human" || !!agentId); // human agent is optional

  const place = async () => {
    if (!selectedLead) return;
    setError(null);
    if (callType === "human") {
      onStartHuman(selectedLead, agentId || undefined);
      onClose();
      return;
    }
    setPlacing(true);
    try {
      await onPlaceAi(selectedLead.id, agentId || undefined);
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo iniciar la llamada");
    } finally {
      setPlacing(false);
    }
  };

  const selectCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;

  return (
    <Modal
      title="Probar llamada a un lead"
      onClose={onClose}
      themeClasses={tc}
      width="max-w-lg"
      content={
        <div className="space-y-4">
          <p className={`text-xs ${tc.textSecondary}`}>
            Elige un lead existente y coloca una llamada de prueba — sin crear un lead nuevo.
          </p>

          {/* Call type */}
          <div>
            <label className={`mb-1.5 block text-xs font-medium ${tc.textSecondary}`}>Tipo de llamada</label>
            <div className="flex gap-2">
              {(
                [
                  { key: "ai", label: "Agente IA", icon: "ri-robot-2-line" },
                  { key: "human", label: "Humano (softphone)", icon: "ri-user-voice-line" },
                ] as const
              ).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setCallType(t.key)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    callType === t.key
                      ? "border-primary-500 bg-primary-500 text-white"
                      : `${tc.border} ${tc.cardBg} ${tc.text} hover:border-primary-500`
                  }`}
                >
                  <i className={t.icon} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Agent picker */}
          <div>
            <label className={`mb-1.5 block text-xs font-medium ${tc.textSecondary}`}>
              {callType === "ai" ? "Agente IA" : "Agente humano (opcional)"}
            </label>
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className={selectCls}>
              {callType === "human" && <option value="">— Sin asignar —</option>}
              {typeAgents.length === 0 && callType === "ai" ? (
                <option value="">Sin agentes IA activos</option>
              ) : (
                typeAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {isDemo(a) ? " · demo (sin provisionar)" : a.product ? ` · ${a.product}` : ""}
                  </option>
                ))
              )}
            </select>
            {selectedAgentIsDemo && (
              <p className="mt-1 text-[11px] text-amber-500">
                <i className="ri-alert-line mr-1" />
                Agente de demo: su agente de ElevenLabs no está provisionado, así que la llamada fallará (document_not_found). Elige un agente real para una llamada que conecte.
              </p>
            )}
          </div>

          {/* Lead directory */}
          <div>
            <label className={`mb-1.5 block text-xs font-medium ${tc.textSecondary}`}>Lead a llamar</label>
            <div className={`mb-2 flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre, empresa o teléfono…"
                className={`w-full bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <div className={`max-h-60 divide-y overflow-y-auto rounded-lg border ${tc.border} ${isDarkMode ? "divide-dark-border" : "divide-gray-100"}`}>
              {loading ? (
                <div className={`p-6 text-center text-sm ${tc.textSecondary}`}>
                  <i className="ri-loader-4-line mr-2 animate-spin" />
                  Cargando leads…
                </div>
              ) : callableLeads.length === 0 ? (
                <div className={`p-6 text-center text-sm ${tc.textSecondary}`}>
                  {leads.length === 0 ? "No hay leads todavía." : "Ningún lead con teléfono coincide."}
                </div>
              ) : (
                callableLeads.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setSelectedLeadId(l.id)}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition ${
                      selectedLeadId === l.id ? "bg-primary-500/10" : isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${
                        selectedLeadId === l.id ? "border-primary-500 bg-primary-500" : tc.border
                      }`}
                    >
                      {selectedLeadId === l.id && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className={`block truncate text-sm font-medium ${tc.text}`}>
                        {l.name || "Lead sin nombre"}
                      </span>
                      <span className={`block truncate text-xs ${tc.textSecondary}`}>
                        {[l.company, leadPhone(l), l.product].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>

          {error && (
            <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg border px-4 py-2 text-sm font-medium ${tc.border} ${tc.cardBg} ${tc.text}`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={place}
              disabled={!canPlace}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className={placing ? "ri-loader-4-line animate-spin" : "ri-phone-fill"} />
              {callType === "ai" ? "Llamar con IA" : "Llamar (softphone)"}
            </button>
          </div>
        </div>
      }
    />
  );
}
